import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'

/**
 * Custom hook for contract (subcontract) operations
 * @param {Object} params - Hook parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.token - Auth token
 * @param {Function} params.onRefresh - Callback to refresh data after operations
 * @param {Function} params.onError - Callback for error handling
 * @param {Function} params.onSuccess - Callback for success handling
 * @param {Function} params.setRowLoading - Callback to set row loading state
 * @param {Function} params.setRowError - Callback to set row error state
 * @param {Function} params.setRowSuccess - Callback to set row success state
 * @param {Function} params.setRowAction - Callback to set row action state
 * @param {Function} params.setLoading - Callback to set global loading state
 * @returns {Object} Hook methods
 */
export const useContractOperations = ({
  projectId,
  token,
  onRefresh,
  onError,
  onSuccess,
  setRowLoading,
  setRowError,
  setRowSuccess,
  setRowAction,
  setLoading
}) => {
  /**
   * Extract error details from error response
   */
  const extractErrorDetails = (err) => {
    const errorData = err.response?.data

    const errorCode = errorData?.accError?.errorCode ||
                     errorData?.errorCode ||
                     errorData?.code ||
                     errorData?.error?.code ||
                     err.code ||
                     err.response?.status

    const errorMsg = errorData?.message ||
                    errorData?.accError?.detail ||
                    errorData?.accError?.title ||
                    errorData?.error?.message ||
                    errorData?.detail ||
                    err.message ||
                    'Operation failed'

    let displayMsg = errorMsg
    if (errorCode) {
      displayMsg = `${errorMsg} (${errorCode})`
    }

    return { errorCode, errorMsg, displayMsg }
  }

  /**
   * Approve a single contract
   */
  const approveContract = async (contract) => {
    const id = contract.id
    const comment = window.prompt('Enter approval comment (optional):', '')

    if (comment === null) return // User cancelled

    setRowError(prev => ({ ...prev, [id]: null }))
    setRowSuccess(prev => ({ ...prev, [id]: null }))
    setRowLoading(prev => ({ ...prev, [id]: true }))
    setRowAction(prev => ({ ...prev, [id]: 'approve' }))

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cost/${projectId}/contracts/${id}/approve`,
        { comment: comment?.trim() || 'Approved' },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.success) {
        setRowSuccess(prev => ({ ...prev, [id]: 'Approved' }))
        onSuccess(`Contract ${contract.code || id} approved successfully.`)

        // Refresh the list
        setTimeout(() => {
          onRefresh()
        }, 1500)
      } else {
        throw new Error(response.data.message || 'Approval failed')
      }

    } catch (err) {
      const { displayMsg } = extractErrorDetails(err)
      setRowError(prev => ({ ...prev, [id]: displayMsg }))
      onError(displayMsg)
    } finally {
      setRowLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  /**
   * Reject a single contract
   */
  const rejectContract = async (contract) => {
    const id = contract.id
    const comment = window.prompt('Enter rejection reason:', '')

    if (comment === null) return // User cancelled

    if (!comment || comment.trim() === '') {
      alert('Rejection reason is required')
      return
    }

    const confirmed = window.confirm(`Are you sure you want to reject contract ${contract.code || id} with reason: "${comment}"?`)
    if (!confirmed) return

    setRowError(prev => ({ ...prev, [id]: null }))
    setRowSuccess(prev => ({ ...prev, [id]: null }))
    setRowLoading(prev => ({ ...prev, [id]: true }))
    setRowAction(prev => ({ ...prev, [id]: 'reject' }))

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cost/${projectId}/contracts/${id}/reject`,
        { comment: comment.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.success) {
        setRowSuccess(prev => ({ ...prev, [id]: 'Rejected' }))
        onSuccess(`Contract ${contract.code || id} rejected successfully.`, 'reject')

        // Refresh the list
        setTimeout(() => {
          onRefresh()
        }, 1500)
      } else {
        throw new Error(response.data.message || 'Rejection failed')
      }

    } catch (err) {
      const { displayMsg } = extractErrorDetails(err)
      setRowError(prev => ({ ...prev, [id]: displayMsg }))
      onError(displayMsg)
    } finally {
      setRowLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  /**
   * Bulk approve contracts
   */
  const bulkApproveContracts = async (selectedIds) => {
    const userComment = window.prompt('Enter approval comment (optional):', '')
    if (userComment === null) return // User cancelled
    const comment = userComment?.trim() || 'Approved'

    setLoading(true)

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cost/${projectId}/contracts/bulk-approve`,
        { contractIds: selectedIds, comment },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const result = response.data.results
      const successCount = result.successful.length
      const failureCount = result.failed.length

      onSuccess(`Bulk approval finished. Success: ${successCount} — Failed: ${failureCount}`)

      // Refresh the list if any succeeded
      if (successCount > 0) {
        setTimeout(() => {
          onRefresh()
        }, 1500)
      }
    } catch (err) {
      const { errorCode, errorMsg } = extractErrorDetails(err)
      let displayMsg = `Bulk approval failed: ${errorMsg}`
      if (errorCode) {
        displayMsg = `${displayMsg} (${errorCode})`
      }
      onError(displayMsg)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Bulk reject contracts
   */
  const bulkRejectContracts = async (selectedIds) => {
    const reason = window.prompt('Enter rejection reason for all selected contracts:', '')

    if (reason === null) return // User cancelled

    if (!reason || reason.trim() === '') {
      alert('Rejection reason is required')
      return
    }

    const confirmed = window.confirm(`Are you sure you want to reject ${selectedIds.length} selected contract(s) with reason: "${reason}"?`)
    if (!confirmed) return

    setLoading(true)

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cost/${projectId}/contracts/bulk-reject`,
        { contractIds: selectedIds, comment: reason.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const result = response.data.results
      const successCount = result.successful.length
      const failureCount = result.failed.length

      onSuccess(`Bulk rejection finished. Success: ${successCount} — Failed: ${failureCount}`, 'reject')

      // Refresh the list if any succeeded
      if (successCount > 0) {
        setTimeout(() => {
          onRefresh()
        }, 1500)
      }
    } catch (err) {
      const { errorCode, errorMsg } = extractErrorDetails(err)
      let displayMsg = `Bulk rejection failed: ${errorMsg}`
      if (errorCode) {
        displayMsg = `${displayMsg} (${errorCode})`
      }
      onError(displayMsg)
    } finally {
      setLoading(false)
    }
  }

  return {
    approveContract,
    rejectContract,
    bulkApproveContracts,
    bulkRejectContracts
  }
}
